import React, { useState, useEffect } from 'react';
import { useSearch } from '../../../shared/contexts/SearchContext';
import { SimpleTagChip, SimpleTagGroup, TagChipLegend } from '../../../shared/ui/components/SimpleTagChip';
import { getFilterDescription, hasActiveFilters, generateTagId } from '../utils/simpleFilterUtils';

// Icons for controls
const SearchIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ClearIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SettingsIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ListIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const GridIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const MasonryIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h6v6H4V4zM12 4h8v4h-8V4zM4 12h8v8H4v-8zM14 10h6v10h-6V10z" />
  </svg>
);

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUpIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

interface SimpleFilterBarProps {
  /** Available tags that can be selected */
  availableTags?: Array<{ name: string; category?: string; count?: number }>;
  /** Whether to show the search input */
  showSearch?: boolean;
  /** Whether to show the help legend */
  showLegend?: boolean;
  /** Custom placeholder for search input */
  searchPlaceholder?: string;
  /** Callback when a tag is clicked from available tags */
  onTagSelect?: (tagName: string, category?: string) => void;
  /** Whether to show view mode toggle */
  showViewModeToggle?: boolean;
  /** Compact mode - reduces spacing and sizing */
  compact?: boolean;
  className?: string;
}

/**
 * A comprehensive filter bar that combines search, active filter display, 
 * and available tag selection with visual logic indicators
 */
export const SimpleFilterBar: React.FC<SimpleFilterBarProps> = ({
  availableTags = [],
  showSearch = true,
  showLegend = false,
  searchPlaceholder = "Search...",
  onTagSelect,
  showViewModeToggle = false,
  compact = false,
  className = ''
}) => {
  const { filterState, filterDispatch, viewMode, setViewMode } = useSearch();
  const [searchInput, setSearchInput] = useState(filterState.searchTerm);
  const [showSettings, setShowSettings] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);

  // Sync search input with state
  useEffect(() => {
    setSearchInput(filterState.searchTerm);
  }, [filterState.searchTerm]);

  // Handle search input submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    filterDispatch({ type: 'SET_SEARCH_TERM', term: searchInput.trim() });
  };

  // Handle search input changes (with debouncing)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== filterState.searchTerm) {
        filterDispatch({ type: 'SET_SEARCH_TERM', term: searchInput.trim() });
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput, filterState.searchTerm, filterDispatch]);

  // Handle tag click/toggle
  const handleTagClick = (tagId: string, value: string, category?: string) => {
    filterDispatch({ 
      type: 'TOGGLE_TAG_MODE', 
      tagId, 
      value, 
      category 
    });
  };

  // Handle available tag selection
  const handleAvailableTagClick = (tagName: string, category?: string) => {
    const tagId = generateTagId(tagName, 'inclusive');
    filterDispatch({
      type: 'TOGGLE_TAG_MODE',
      tagId,
      value: tagName,
      category
    });
    
    if (onTagSelect) {
      onTagSelect(tagName, category);
    }
  };

  // Clear all filters
  const handleClearAll = () => {
    filterDispatch({ type: 'RESET_FILTERS' });
    setSearchInput('');
  };

  // Toggle global logic
  const handleGlobalLogicToggle = () => {
    filterDispatch({
      type: 'SET_GLOBAL_LOGIC',
      logic: filterState.globalLogic === 'AND' ? 'OR' : 'AND'
    });
  };

  // Group active tags by category
  const groupActiveTagsByCategory = () => {
    const grouped = Object.entries(filterState.tags).reduce((acc, [tagId, tag]) => {
      const category = tag.category || 'uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push({ id: tagId, value: tag.value, mode: tag.mode, category: tag.category });
      return acc;
    }, {} as Record<string, Array<{ id: string; value: string; mode: 'inclusive' | 'required' | 'excluded'; category?: string }>>);

    return grouped;
  };

  // Sort and limit available tags by importance (usage count)
  const getSortedAvailableTags = () => {
    // Sort by count (highest first), then alphabetically
    const sorted = [...availableTags].sort((a, b) => {
      // If both have counts, sort by count (descending)
      if (a.count !== undefined && b.count !== undefined) {
        if (a.count !== b.count) {
          return b.count - a.count; // Higher count first
        }
      }
      // If only one has count, prioritize the one with count
      if (a.count !== undefined && b.count === undefined) return -1;
      if (a.count === undefined && b.count !== undefined) return 1;
      
      // If neither have counts or counts are equal, sort alphabetically
      return a.name.localeCompare(b.name);
    });

    return showAllTags ? sorted : sorted.slice(0, 8);
  };

  // Group available tags by category
  const groupAvailableTagsByCategory = () => {
    const sortedTags = getSortedAvailableTags();
    return sortedTags.reduce((acc, tag) => {
      const category = tag.category || 'uncategorized';
      if (!acc[category]) acc[category] = [];
      acc[category].push(tag);
      return acc;
    }, {} as Record<string, Array<{ name: string; category?: string; count?: number }>>);
  };

  const activeTagGroups = groupActiveTagsByCategory();
  const availableTagGroups = groupAvailableTagsByCategory();
  const hasFilters = hasActiveFilters(filterState);
  const sortedTags = getSortedAvailableTags();
  const hasMoreTags = availableTags.length > 8;

  return (
    <div className={`space-y-3 ${compact ? 'space-y-2' : ''} ${className}`}>
      {/* Search Bar */}
      {showSearch && (
        <form onSubmit={handleSearchSubmit} className="relative">
          <div className="bg-white rounded-lg">
            <div className="relative flex items-center">
              <SearchIcon className="absolute left-3 sm:left-6 top-1/2 transform -translate-y-1/2 h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={searchPlaceholder}
                className={`
                  flex-1 pl-8 sm:pl-12 pr-16 sm:pr-20 py-2 sm:py-3 outline-none bg-transparent text-gray-900 placeholder-gray-500 font-medium
                  ${compact ? 'text-sm py-2' : 'text-sm sm:text-base'}
                `}
              />
              
              {/* View Mode Toggle */}
              {showViewModeToggle && (
                <div className="absolute right-2 sm:right-6 top-1/2 transform -translate-y-1/2 flex items-center gap-0.5 sm:gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 sm:p-2 rounded border-2 border-black font-bold transition-colors ${
                      viewMode === 'grid' 
                        ? "bg-blue-300 text-black" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    title="Grid view"
                    aria-label="Grid view"
                  >
                    <GridIcon className="w-3 sm:w-4 h-3 sm:h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('masonry')}
                    className={`p-1.5 sm:p-2 rounded border-2 border-black font-bold transition-colors ${
                      viewMode === 'masonry' 
                        ? "bg-blue-300 text-black" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    title="Masonry view"
                    aria-label="Masonry view"
                  >
                    <MasonryIcon className="w-3 sm:w-4 h-3 sm:h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 sm:p-2 rounded border-2 border-black font-bold transition-colors ${
                      viewMode === 'list' 
                        ? "bg-blue-300 text-black" 
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                    title="List view"
                    aria-label="List view"
                  >
                    <ListIcon className="w-3 sm:w-4 h-3 sm:h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Active Filters Display */}
      {hasFilters && (
        <div className={`border-2 border-black rounded-lg bg-white ${compact ? 'p-2' : 'p-4'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`font-bold uppercase text-black ${compact ? 'text-sm' : 'text-base'}`}>
              🎯 Active Filters
            </h3>
            <div className="flex items-center gap-2">
              {/* Global Logic Toggle (only show if multiple tags) */}
              {Object.keys(filterState.tags).length > 1 && (
                <button
                  onClick={handleGlobalLogicToggle}
                  className={`
                    px-3 py-1.5 text-xs font-bold uppercase rounded-full border-2 border-black transition-colors
                    ${filterState.globalLogic === 'AND' 
                      ? 'bg-green-300 text-black hover:bg-green-400' 
                      : 'bg-blue-300 text-black hover:bg-blue-400'
                    }
                  `}
                  title={`Global logic: ${filterState.globalLogic} - Click to toggle`}
                >
                  {filterState.globalLogic}
                </button>
              )}
              
              {/* Settings Toggle */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`
                  p-2 rounded font-bold transition-colors
                  ${showSettings ? 'bg-orange-300 text-black' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}
                `}
                title="Filter settings"
              >
                <SettingsIcon className="h-4 w-4" />
              </button>

              {/* Clear All Button */}
              <button
                onClick={handleClearAll}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase bg-red-300 text-black rounded-full transition-colors hover:bg-red-400"
                title="Clear all filters"
              >
                <ClearIcon className="h-3 w-3" />
                CLEAR ALL
              </button>
            </div>
          </div>

          {/* Filter Description */}
          <div className={`bg-yellow-100 rounded-lg p-2 mb-3 ${compact ? 'text-xs' : 'text-sm'}`}>
            <p className="font-medium text-black">
              📝 {getFilterDescription(filterState)}
            </p>
          </div>

          {/* Active Tags by Category */}
          <div className="space-y-3">
            {Object.entries(activeTagGroups).map(([category, tags]) => (
              <SimpleTagGroup
                key={category}
                title={category !== 'uncategorized' ? category : undefined}
                tags={tags}
                onTagClick={handleTagClick}
                className={compact ? 'space-y-1' : ''}
              />
            ))}
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              {showLegend && <TagChipLegend className="mb-3" />}
              <div className="bg-blue-100 rounded-lg p-3">
                <h4 className="font-bold uppercase text-black text-sm mb-2">💡 HOW TO USE:</h4>
                <div className="text-xs text-black space-y-1">
                  <p>• Click tags to cycle: OR → AND → NOT → Remove</p>
                  <p>• Use global logic to control multiple tags</p>
                  <p>• Search combines with tag filters</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Available Tags (if provided) - Collapsible */}
      {availableTags.length > 0 && (
        <div className="border-2 border-black rounded-lg bg-white">
          <button
            onClick={() => setShowAllTags(!showAllTags)}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <h4 className={`font-bold uppercase text-black ${compact ? 'text-sm' : 'text-base'}`}>
              🏷️ Available Tags ({availableTags.length})
            </h4>
            {showAllTags ? (
              <ChevronUpIcon className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDownIcon className="w-5 h-5 text-gray-600" />
            )}
          </button>
          
          {(showAllTags || !hasMoreTags) && (
            <div className="px-4 pb-4 border-t border-gray-200">
              <div className="space-y-3">
                {Object.entries(availableTagGroups).map(([category, tags]) => (
                  <div key={category}>
                    {category !== 'uncategorized' && (
                      <h5 className="text-xs font-bold uppercase text-gray-600 mb-2 tracking-wide">
                        {category}
                      </h5>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => (
                        <button
                          key={tag.name}
                          onClick={() => handleAvailableTagClick(tag.name, tag.category)}
                          className={`
                            px-3 py-1.5 text-xs font-bold uppercase rounded-full transition-colors
                            bg-yellow-300 text-black hover:bg-yellow-400
                            ${compact ? 'text-xs px-2 py-1' : ''}
                          `}
                          title={`Add ${tag.name} as filter${tag.count ? ` (used in ${tag.count} references)` : ''}`}
                        >
                          {tag.name}
                          {tag.count !== undefined && tag.count > 0 && (
                            <span className="ml-1 text-xs opacity-75">({tag.count})</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Show first few tags when collapsed */}
              {!showAllTags && hasMoreTags && (
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-500">
                    Showing top {sortedTags.length} most-used tags of {availableTags.length} total
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Preview when collapsed */}
          {!showAllTags && hasMoreTags && (
            <div className="px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {sortedTags.slice(0, 6).map((tag) => (
                  <button
                    key={tag.name}
                    onClick={() => handleAvailableTagClick(tag.name, tag.category)}
                    className="px-3 py-1.5 text-xs font-bold uppercase rounded-full transition-colors bg-yellow-300 text-black hover:bg-yellow-400"
                    title={`Add ${tag.name} as filter${tag.count ? ` (used in ${tag.count} references)` : ''}`}
                  >
                    {tag.name}
                    {tag.count !== undefined && tag.count > 0 && (
                      <span className="ml-1 text-xs opacity-75">({tag.count})</span>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => setShowAllTags(true)}
                  className="px-3 py-1.5 text-xs font-bold uppercase rounded-full transition-colors bg-orange-300 text-black hover:bg-orange-400"
                >
                  +{availableTags.length - 6} MORE
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Legend */}
      {showLegend && !hasFilters && (
        <div className="bg-white rounded-lg p-4">
          <TagChipLegend />
        </div>
      )}
    </div>
  );
}; 